/* ============================================================================
   AKINOD · Landings de campagne : comportements
   ----------------------------------------------------------------------------
   Chargé par `layout/landing.liquid` seulement. Quatre gestes : trois repris de
   la maquette — la galerie de la fiche produit, la copie du code promo et
   l'apparition des blocs au défilement — puis la mémoire de visite, que lit
   `akinod-popup.js` pour ne pas remontrer l'offre à qui l'a déjà lue. Les trois
   premiers se réarment au rechargement d'une section dans l'éditeur de thème ;
   la visite, elle, ne se note qu'une fois.
   ========================================================================== */

(() => {
  const REDUIT = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* --- Galerie : une vignette remplace le visuel principal ------------------ */

  function initGaleries(racine) {
    racine.querySelectorAll('[data-ak-lp-galerie]:not([data-ak-lp-galerie-pret])').forEach((galerie) => {
      galerie.dataset.akLpGaleriePret = '1';
      const principale = galerie.querySelector('.ak-lp-principale');
      const vignettes = galerie.querySelectorAll('[data-ak-lp-vignette]');
      if (!principale) return;

      vignettes.forEach((bouton) => {
        bouton.addEventListener('click', () => {
          const img = bouton.querySelector('img');
          principale.srcset = bouton.dataset.srcset || '';
          principale.src = bouton.dataset.src;
          principale.alt = img ? img.alt : '';
          vignettes.forEach((autre) => autre.setAttribute('aria-pressed', String(autre === bouton)));
        });
      });
    });
  }

  /* --- Copie du code promo ---------------------------------------------------
     L'API presse-papiers d'abord. Si elle manque ou refuse (contexte non
     sécurisé, document sans le focus, ancien navigateur), la sélection d'un
     champ caché prend le relais. La confirmation ne s'affiche que si l'une des
     deux a réellement copié. */

  function copierParSelection(texte) {
    const champ = document.createElement('textarea');
    champ.value = texte;
    champ.setAttribute('readonly', '');
    champ.style.position = 'fixed';
    champ.style.top = '0';
    champ.style.opacity = '0';
    document.body.append(champ);
    champ.select();
    champ.setSelectionRange(0, texte.length);
    let reussi = false;
    try {
      reussi = document.execCommand('copy');
    } catch (erreur) {
      reussi = false;
    }
    champ.remove();
    return reussi;
  }

  async function copier(texte) {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(texte);
        return true;
      } catch (erreur) {
        // voie de secours ci-dessous
      }
    }
    return copierParSelection(texte);
  }

  function initCodes(racine) {
    racine.querySelectorAll('[data-ak-lp-code]:not([data-ak-lp-code-pret])').forEach((bouton) => {
      bouton.dataset.akLpCodePret = '1';
      const statut = bouton.parentElement.querySelector('[data-ak-lp-statut]');

      bouton.addEventListener('click', async () => {
        if (!(await copier(bouton.dataset.akLpCode))) return;
        bouton.classList.add('is-copie');
        if (statut) statut.textContent = bouton.dataset.copie;
        window.clearTimeout(bouton.akLpMinuterie);
        bouton.akLpMinuterie = window.setTimeout(() => {
          bouton.classList.remove('is-copie');
          if (statut) statut.textContent = '';
        }, 2200);
      });
    });
  }

  /* --- Apparition au défilement ----------------------------------------------
     Même filet de sécurité que le reste du thème : si l'observateur ne répond
     jamais (onglet en arrière-plan, rendu hors écran), tout est découvert. */

  function initReveal(racine) {
    const blocs = racine.querySelectorAll('.ak-lp-reveal:not([data-ak-lp-reveal-pret])');
    if (!blocs.length) return;
    blocs.forEach((bloc) => { bloc.dataset.akLpRevealPret = '1'; });

    if (REDUIT || !('IntersectionObserver' in window) || window.Shopify?.designMode) {
      blocs.forEach((bloc) => bloc.classList.add('is-visible'));
      return;
    }

    let aRepondu = false;
    const observateur = new IntersectionObserver((entrees) => {
      aRepondu = true;
      entrees.forEach((entree) => {
        if (!entree.isIntersecting) return;
        entree.target.classList.add('is-visible');
        observateur.unobserve(entree.target);
      });
    }, { threshold: 0.15 });

    blocs.forEach((bloc) => observateur.observe(bloc));
    window.setTimeout(() => {
      if (aRepondu) return;
      observateur.disconnect();
      blocs.forEach((bloc) => bloc.classList.add('is-visible'));
    }, 1200);
  }

  /* --- Mémoire de visite ------------------------------------------------------
     La landing note son passage : la pop-up de la même campagne ne se montre
     plus à qui a déjà lu l'offre (`akinod-popup.js`). Adresse sans préfixe de
     langue, pour qu'une visite en /en compte aussi sur le site français. */

  function noterLaVisite() {
    if (window.Shopify?.designMode) return;
    const chemin = window.location.pathname.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/|$)/i, '') || '/';
    try {
      window.localStorage.setItem(`akinod-landing-vue:${chemin}`, String(Date.now()));
    } catch (erreur) {
      // sans stockage, la pop-up se remontrera : gênant, jamais faux
    }
  }

  noterLaVisite();

  /* Chaque initialisation a son propre drapeau : un même bloc peut être à la
     fois une galerie et un bloc qui apparaît au défilement, et un drapeau
     commun ferait sauter la seconde initialisation. */
  function demarrer(racine) {
    initGaleries(racine);
    initCodes(racine);
    initReveal(racine);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => demarrer(document));
  } else {
    demarrer(document);
  }

  document.addEventListener('shopify:section:load', (evenement) => demarrer(evenement.target));
})();
